import { Box, Typography } from "@mui/material";
import html2pdf from "html2pdf.js";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import useColors from "../../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../../api/hooks/useIsIntegrated";
import TextButton from "../../../../../components/buttons/TextButton";
import LoadingIcon from "../../../../../components/LoadingIcon";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { severities } from "../../../../stack/environment/icons/iconExport";
import {
  IntegrationCategory,
  IntegrationsMapping,
} from "../../../../stack/integrations/IntegrationsMapping";
import {
  createPdfConfig,
  createPdfContainer,
  createPdfHeader,
  measureContentForPdf,
} from "../../../custom_dashboard/utils/commonWidgetUtils";
import WidgetWrapper from "../../../custom_dashboard/WidgetWrapper";
import AIIntegrationsEmptyState from "./AIIntegrationsEmptyState";

const AIIntegrationsWidget = forwardRef(
  ({ registerWidgetExport, widgetId }, ref) => {
    const colors = useColors();
    const widgetRef = useRef(null);

    const { integrations, isIntegratedLoading } = useIsIntegrated();

    const isIntegrated = (codeName) => {
      return (integrations || []).includes(codeName);
    };
    const AI_INTEGRATIONS = Object.values(IntegrationsMapping).filter(
      (integration) =>
        integration.available === true &&
        integration.category === IntegrationCategory.AI &&
        isIntegrated(integration.codeName)
    );
    const styles = {
      container: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        padding: "0px 20px 20px 20px",
      },
      integrationsList: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      },
      integrationCard: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        backgroundColor: colors.common_white,
        borderRadius: "8px",
        border: `1px solid ${colors.grey_24percent}`,
        transition: "all 0.15s ease",
        cursor: "default",
      },
      integrationInfo: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
      },
      integrationIcon: {
        width: "32px",
        height: "32px",
        borderRadius: "6px",
        backgroundColor: colors.common_white,
        border: `1px solid ${colors.grey_24percent}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "6px",
        flexShrink: 0,
        "& img": {
          width: "100%",
          height: "100%",
          objectFit: "contain",
        },
      },
      integrationName: {
        fontWeight: 500,
        fontSize: "14px",
        lineHeight: "20px",
        color: colors.grey_900,
        letterSpacing: "-0.006em",
      },
      subtitle: {
        color: colors.grey_600,
        fontSize: "13px",
        lineHeight: "20px",
        fontWeight: 400,
        paddingBottom: "8px",
      },
    };

    const handleConnectMoreAIIntegrations = () => {
      sessionStorage.setItem("integrationFilter", "AI");
      window.open("/inventory/integrations", "_blank");
    };

    const exportToPdf = async (isCombinedExport = false) => {
      if (!widgetRef.current) return null;

      try {
        const clonedElement = widgetRef.current.cloneNode(true);
        clonedElement.style.width = "800px";
        clonedElement.style.maxWidth = "800px";
        clonedElement.style.backgroundColor = "#ffffff";
        clonedElement.style.padding = "24px";
        clonedElement.style.borderRadius = "12px";
        clonedElement.style.boxShadow = "0px 2px 8px rgba(0, 0, 0, 0.1)";

        if (isCombinedExport) {
          return clonedElement;
        }

        const fullElement = await createPdfContainer();
        const header = createPdfHeader(
          "AI Integrations",
          "AI Posture Dashboard"
        );
        fullElement.appendChild(header);
        fullElement.appendChild(clonedElement);

        const { contentWidth, contentHeight } = await measureContentForPdf(
          fullElement
        );

        const pdfConfig = createPdfConfig(
          "AI_Integrations_Widget.pdf",
          contentWidth,
          contentHeight
        );

        await html2pdf().set(pdfConfig).from(fullElement).save();

        return null;
      } catch (error) {
        console.error("Error exporting to PDF:", error);
        return null;
      }
    };

    useImperativeHandle(ref, () => ({
      exportToPdf,
      hasExportCapability: true,
    }));

    useEffect(() => {
      if (registerWidgetExport && widgetId) {
        registerWidgetExport(widgetId, exportToPdf);
      }
    }, [registerWidgetExport, widgetId]);

    return (
      <WidgetWrapper title="AI Integrations">
        <Box
          ref={widgetRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: "24px",
            height: "100%",
          }}
        >
          {isIntegratedLoading ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <LoadingIcon size={8} />
            </Box>
          ) : AI_INTEGRATIONS.length === 0 ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <AIIntegrationsEmptyState
                onClick={handleConnectMoreAIIntegrations}
              />
            </Box>
          ) : (
            <Box sx={styles.container}>
              <Box sx={styles.integrationsList}>
                <Typography sx={styles.subtitle}>
                  Integrations monitored in your environment
                </Typography>
                {AI_INTEGRATIONS.map((integration) => (
                  <Box
                    key={integration.displayName}
                    sx={styles.integrationCard}
                  >
                    <Box sx={styles.integrationInfo}>
                      <Box sx={styles.integrationIcon}>
                        <SvgToIcon
                          IconLogo={integration.logo}
                          width="32px"
                          height="32px"
                        />
                      </Box>
                      <Typography sx={styles.integrationName}>
                        {integration.displayName}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "4px 10px",
                        borderRadius: "16px",
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                    >
                      <SvgToIcon
                        IconLogo={severities.SeveritySuccessIcon}
                        width="16px"
                        height="16px"
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: colors.action_active }}
                      >
                        Monitored
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <TextButton
                  onClick={handleConnectMoreAIIntegrations}
                  color="primary"
                  size="small"
                >
                  Connect More AI Integrations
                </TextButton>
              </Box>
            </Box>
          )}
        </Box>
      </WidgetWrapper>
    );
  }
);

AIIntegrationsWidget.displayName = "AIIntegrationsWidget";

export default AIIntegrationsWidget;
